import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth } from '@/contexts/Auth0Context'
import { messagingSocket } from '@/services/messagingSocket'
import { MessagingSocketEvent } from '@/types/messaging'

interface MessagingSocketContextValue {
  isConnected: boolean
  on: <T = unknown>(event: MessagingSocketEvent, handler: (payload: T) => void) => () => void
  joinRoom: (roomId: string) => void
  leaveRoom: (roomId: string) => void
}

const MessagingSocketContext = createContext<MessagingSocketContextValue | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useMessagingSocket = (): MessagingSocketContextValue => {
  const ctx = useContext(MessagingSocketContext)
  if (!ctx) throw new Error('useMessagingSocket must be used within MessagingSocketProvider')
  return ctx
}

interface MessagingSocketProviderProps {
  children: ReactNode
}

export const MessagingSocketProvider: React.FC<MessagingSocketProviderProps> = ({ children }) => {
  const { isAuthenticated, getAccessToken } = useAuth()
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) {
      messagingSocket.disconnect()
      setIsConnected(false)
      return
    }

    messagingSocket.setTokenGetter(() => getAccessToken())

    let cancelled = false
    messagingSocket
      .connect()
      .then(() => {
        if (!cancelled) setIsConnected(true)
      })
      .catch((err) => {
        console.error('Messaging socket connection failed:', err)
        if (!cancelled) setIsConnected(false)
      })

    return () => {
      cancelled = true
      messagingSocket.disconnect()
      setIsConnected(false)
    }
  }, [isAuthenticated, getAccessToken])

  const value: MessagingSocketContextValue = {
    isConnected,
    on: (event, handler) => messagingSocket.on(event, handler),
    joinRoom: (roomId) => messagingSocket.joinRoom(roomId),
    leaveRoom: (roomId) => messagingSocket.leaveRoom(roomId)
  }

  return (
    <MessagingSocketContext.Provider value={value}>{children}</MessagingSocketContext.Provider>
  )
}
